<script>
import SpecTypeForm from '@/components/fulfillment/SpecTypeForm.vue'
import CandidateList from '@/components/fulfillment/CandidateList.vue'
import PageGuideDialog from '@/components/admin/fulfillment/guide/PageGuideDialog.vue'
import PartnerFulfillmentGuide from '@/components/admin/fulfillment/guide/PartnerFulfillmentGuide.vue'

export default {
  name: 'PartnerSkuRequestNew',
  props: {
    inModal: {
      type: Boolean,
      default: false
    }
  },
  emits: ['created', 'close'],
  components: {
    SpecTypeForm,
    CandidateList,
    PageGuideDialog,
    PartnerFulfillmentGuide
  },
  data() {
    return {
      masterDataLoading: false,
      brands: [],
      categories: [],
      packUnitTypes: [],
      form: {
        partner_id: '',
        partner_sku_code: '',
        barcode: '',
        brand_id: '',
        brand_name: '',
        category_id: '',
        category_name: '',
        hs_code: '',
        spec_type: '',
        spec_detail: {}
      },
      candidates: [],
      submitCandidates: [],
      selectedCandidateId: null,
      submitting: false,
      candidateLoading: false,
      duplicateDialogOpen: false,
      guideDialog: false,
      pendingSubmitPayload: null,
      specTypeOptions: [
        { label: 'APPAREL_SIZE', value: 'APPAREL_SIZE' },
        { label: 'PRODUCT_OPTION', value: 'PRODUCT_OPTION' },
        { label: 'PACK_COUNT', value: 'PACK_COUNT' },
      ],
      useCustomBrand: false,
      useCustomCategory: false,
    }
  },
  computed: {
    duplicateActionLabel() {
      return this.submitCandidates.length > 0 ? '연결 요청 제출' : '그래도 요청 제출'
    }
  },
  methods: {
    getResolvedBrandId() {
      if (this.form.brand_id) return this.form.brand_id
      const normalizedName = String(this.form.brand_name || '').trim().toLowerCase()
      if (!normalizedName) return ''
      const found = this.brands.find(item => String(item.name || '').trim().toLowerCase() === normalizedName)
      return found?.brand_id || ''
    },
    getResolvedCategoryId() {
      if (this.form.category_id) return this.form.category_id
      const normalizedName = String(this.form.category_name || '').trim().toLowerCase()
      if (!normalizedName) return ''
      const found = this.categories.find(item => String(item.name || '').trim().toLowerCase() === normalizedName)
      return found?.category_id || ''
    },
    normalizeSpecDetail() {
      const type = String(this.form.spec_type || '').toUpperCase()
      const detail = this.form.spec_detail || {}

      if (type === 'DIMENSION') {
        return {
          length: detail.length ?? detail.length_cm ?? null,
          width: detail.width ?? detail.width_cm ?? null,
          height: detail.height ?? detail.height_cm ?? null,
        }
      }

      if (type === 'VOLUME') {
        return {
          value: detail.value ?? detail.volume_value ?? null,
          unit: detail.unit ?? detail.volume_unit ?? null,
        }
      }

      if (type === 'APPAREL_SIZE') {
        return {
          size: detail.size ?? detail.apparel_size ?? null,
        }
      }

      if (type === 'PRODUCT_OPTION') {
        return {
          model_code: detail.model_code ?? detail.model ?? null,
          color: detail.color ?? null,
          size: detail.size ?? detail.size_value ?? null,
        }
      }

      if (type === 'PACK_COUNT') {
        return {
          unit: detail.unit ?? detail.pack_unit_type_code ?? null,
          count: detail.count ?? detail.pack_count ?? null,
          pack_uom: detail.pack_uom ?? 'ea',
        }
      }

      return detail
    },
    closeModal() {
      this.$emit('close')
    },
    async loadMasterData() {
      this.masterDataLoading = true

      const brandConfig = {
        method: 'GET',
        url: '/api/fulfillment/brands',
        name: '[ Fulfillment / 브랜드 목록 ]'
      }
      const categoryConfig = {
        method: 'GET',
        url: '/api/fulfillment/categories',
        name: '[ Fulfillment / 카테고리 목록 ]'
      }
      const packUnitConfig = {
        method: 'GET',
        url: '/api/fulfillment/pack-unit-types',
        name: '[ Fulfillment / 패키지 단위 목록 ]'
      }

      await this.$http(brandConfig)
        .then(res => { this.brands = res.data?.data || [] })
        .catch(() => { this.brands = [] })

      await this.$http(categoryConfig)
        .then(res => { this.categories = res.data?.data || [] })
        .catch(() => { this.categories = [] })

      await this.$http(packUnitConfig)
        .then(res => { this.packUnitTypes = res.data?.data || [] })
        .catch(() => { this.packUnitTypes = [] })

      this.masterDataLoading = false
    },
    normalizeDefaultSpecType(value) {
      const allowed = ['APPAREL_SIZE', 'PRODUCT_OPTION', 'PACK_COUNT']
      return allowed.includes(value) ? value : 'PRODUCT_OPTION'
    },
    onCategoryChange(val) {
      const found = this.categories.find(item => String(item.category_id) === String(val))
      if (found && found.default_spec_type) {
        this.form.spec_type = this.normalizeDefaultSpecType(found.default_spec_type)
        this.form.spec_detail = {}
      }
    },
    validateForm() {
      if (!this.form.partner_sku_code) {
        this.notify('partner_sku_code는 필수입니다.')
        return false
      }
      if (this.form.barcode && !/^[0-9]{1,64}$/.test(this.form.barcode)) {
        this.notify('바코드번호는 숫자만 입력할 수 있습니다.')
        return false
      }
      if (!/^[0-9]{6}$/.test(this.form.hs_code || '')) {
        this.notify('HS Code는 6자리 숫자여야 합니다.')
        return false
      }
      if ((!this.form.brand_id && !String(this.form.brand_name || '').trim()) || (!this.form.category_id && !String(this.form.category_name || '').trim()) || !this.form.spec_type) {
        this.notify('브랜드/카테고리/spec_type을 입력하세요.')
        return false
      }
      return true
    },
    sanitizeBarcode(value) {
      this.form.barcode = String(value || '').replace(/\D+/g, '').slice(0, 64)
    },
    notify(message, type = 'negative') {
      if (this.$q && this.$q.notify) {
        this.$q.notify({ message, type })
        return
      }
      alert(message)
    },
    buildCandidateRequestPayload() {
      const resolvedBrandId = this.getResolvedBrandId()
      const resolvedCategoryId = this.getResolvedCategoryId()
      if (!resolvedBrandId || !resolvedCategoryId) {
        return null
      }
      const normalizedSpecDetail = this.normalizeSpecDetail()
      const spec_key = this.$makeSpecKey(this.form.spec_type, normalizedSpecDetail)
      if (!spec_key) {
        return null
      }
      return {
        brand_id: resolvedBrandId,
        category_id: resolvedCategoryId,
        hs_code: this.form.hs_code,
        spec_type: this.form.spec_type,
        spec_key
      }
    },
    async requestCandidates(payload) {
      const config = {
        method: 'POST',
        url: '/api/fulfillment/master-skus/candidates',
        data: payload,
        name: '[ Fulfillment / Master SKU 후보 조회 ]'
      }
      const res = await this.$http(config)
      return res.data?.data || []
    },
    async fetchCandidates() {
      if (!this.validateForm()) return
      const candidatePayload = this.buildCandidateRequestPayload()
      if (!candidatePayload) {
        this.notify('동일 SKU 조회는 기존 브랜드/카테고리를 선택하거나, 규격 정보를 정확히 입력해야 합니다.')
        return
      }
      this.candidateLoading = true
      await this.requestCandidates(candidatePayload)
        .then((rows) => { this.candidates = rows })
        .catch(() => { this.candidates = [] })
      this.candidateLoading = false
    },
    onCandidateSelect(masterSkuId) {
      this.selectedCandidateId = masterSkuId
    },
    buildSubmitPayload() {
      const resolvedBrandId = this.getResolvedBrandId()
      const resolvedCategoryId = this.getResolvedCategoryId()
      const normalizedSpecDetail = this.normalizeSpecDetail()
      const spec_key = this.$makeSpecKey(this.form.spec_type, normalizedSpecDetail)
      if (!spec_key) return null

      return {
        partner_id: this.form.partner_id,
        partner_sku_code: this.form.partner_sku_code,
        barcode: this.form.barcode || null,
        brand_id: resolvedBrandId,
        brand_name: String(this.form.brand_name || '').trim(),
        category_id: resolvedCategoryId,
        category_name: String(this.form.category_name || '').trim(),
        hs_code: this.form.hs_code,
        spec_type: this.form.spec_type,
        spec_key,
        spec_detail: normalizedSpecDetail
      }
    },
    async executeSubmit(payload) {
      const config = {
        method: 'POST',
        url: '/api/fulfillment/partner/sku-requests',
        data: payload,
        name: '[ Fulfillment / Partner SKU 요청 생성 ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('요청이 생성되었습니다.', 'positive')
          this.duplicateDialogOpen = false
          this.pendingSubmitPayload = null
          if (this.inModal) {
            this.$emit('created')
            this.$emit('close')
          } else {
            this.$router.push('/partner/sku-requests')
          }
        })
        .catch(() => {
          this.notify('요청 생성에 실패했습니다.')
        })
    },
    async confirmDuplicateSubmit() {
      if (!this.pendingSubmitPayload) return
      this.submitting = true
      await this.executeSubmit(this.pendingSubmitPayload)
      this.submitting = false
    },
    async submitRequest() {
      if (!this.validateForm()) return
      const submitPayload = this.buildSubmitPayload()
      if (!submitPayload) {
        this.notify('spec_detail을 확인하세요.')
        return
      }

      const candidatePayload = this.buildCandidateRequestPayload()
      if (candidatePayload) {
        this.submitting = true
        try {
          const rows = await this.requestCandidates(candidatePayload)
          if (Array.isArray(rows) && rows.length > 0) {
            this.submitCandidates = rows
            this.pendingSubmitPayload = {
              ...submitPayload,
              resolved_master_sku_id: rows[0]?.master_sku_id || null,
            }
            this.duplicateDialogOpen = true
            return
          }
        } catch (_) {
          this.notify('동일 SKU 확인 중 오류가 발생했습니다.')
          return
        } finally {
          this.submitting = false
        }
      }

      this.submitting = true
      await this.executeSubmit(submitPayload)
      this.submitting = false
    },
  },
  mounted() {
    const session = this.$store.getters.getLocalSession || {}
    this.form.partner_id = session.partnerId || session.partner_id || session.id || ''
    this.loadMasterData()
  },
  watch: {
    'form.spec_type'(val) {
      if (!val) return
      this.form.spec_detail = {}
    },
    useCustomBrand(next) {
      if (next) {
        this.form.brand_id = ''
        return
      }
      this.form.brand_name = ''
    },
    useCustomCategory(next) {
      if (next) {
        this.form.category_id = ''
        return
      }
      this.form.category_name = ''
    }
  }
}
</script>

<template>
  <q-layout container class="bg-white">
    <q-header class="bg-primary">
      <q-toolbar>
        <q-toolbar-title class="row items-center">
          <span>SKU 요청 생성</span>
          <q-btn flat round dense size="sm" icon="help_outline" aria-label="SKU 요청 생성 사용 가이드" class="q-ml-xs" @click="guideDialog = true">
            <q-tooltip>SKU 요청 생성 사용 가이드</q-tooltip>
          </q-btn>
        </q-toolbar-title>
        <q-btn flat round dense icon="close" @click="closeModal" />
      </q-toolbar>
    </q-header>

    <q-footer class="bg-white text-black">
      <q-toolbar>
        <div class="text-caption text-grey-7 q-mr-md">
          동일 SKU는 브랜드, 카테고리, HS CODE, 규격 타입, 규격값이 모두 같은 경우입니다.
        </div>
        <q-btn
          color="primary"
          label="동일 SKU 조회"
          :loading="candidateLoading"
          @click="fetchCandidates"
          class="q-mr-sm"
        />
        <q-btn
          color="positive"
          label="요청 제출"
          :loading="submitting"
          @click="submitRequest"
        />
      </q-toolbar>
    </q-footer>

    <q-page-container>
      <q-page padding>
        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-12">
            <q-banner class="bg-grey-2 text-grey-9">
              동일 SKU는 브랜드, 카테고리, HS CODE, 규격 타입, 규격값이 모두 같은 경우입니다.
              상품명만 같아서는 동일 SKU로 판단하지 않습니다.
            </q-banner>
          </div>
          <div class="col-12 col-md-4">
            <q-input
              v-model="form.partner_sku_code"
              label="Partner SKU Code"
              outlined
              dense
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
              v-model="form.barcode"
              label="바코드번호"
              outlined
              dense
              maxlength="64"
              inputmode="numeric"
              @update:model-value="sanitizeBarcode"
            />
          </div>
          <div class="col-12 col-md-4">
            <q-toggle
              v-model="useCustomBrand"
              label="브랜드 직접입력"
              class="q-mb-sm"
            />
            <q-select
              v-if="!useCustomBrand"
              v-model="form.brand_id"
              :options="brands"
              option-label="name"
              option-value="brand_id"
              emit-value
              map-options
              label="Brand"
              outlined
              dense
            />
            <q-input
              v-else
              v-model="form.brand_name"
              label="Brand 직접입력"
              outlined
              dense
            />
          </div>
          <div class="col-12 col-md-4">
            <q-toggle
              v-model="useCustomCategory"
              label="카테고리 직접입력"
              class="q-mb-sm"
            />
            <q-select
              v-if="!useCustomCategory"
              v-model="form.category_id"
              :options="categories"
              option-label="name"
              option-value="category_id"
              emit-value
              map-options
              label="Category"
              outlined
              dense
              @update:model-value="onCategoryChange"
            />
            <q-input
              v-else
              v-model="form.category_name"
              label="Category 직접입력"
              outlined
              dense
            />
          </div>
        </div>

        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-12 col-md-4">
            <q-input
              v-model="form.hs_code"
              label="HS Code (6 digits)"
              outlined
              dense
              maxlength="6"
            />
          </div>
          <div class="col-12 col-md-4">
            <q-select
              v-model="form.spec_type"
              :options="specTypeOptions"
              emit-value
              map-options
              label="Spec Type"
              outlined
              dense
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
              v-model="form.partner_id"
              label="Partner ID"
              outlined
              dense
              disable
            />
          </div>
        </div>

        <div class="row q-mb-md">
          <div class="col">
            <div class="comp-box">
            <div class="text-body2 q-mb-sm">Spec Detail</div>
              <SpecTypeForm
                :spec-type="form.spec_type"
                v-model="form.spec_detail"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <CandidateList
              :candidates="candidates"
              @select="onCandidateSelect"
            />
            <div v-if="selectedCandidateId" class="text-caption text-grey q-mt-sm">
              선택된 동일 SKU ID: {{ selectedCandidateId }}
            </div>
          </div>
        </div>
      </q-page>
    </q-page-container>

    <q-dialog v-model="duplicateDialogOpen">
      <q-card style="min-width: 720px; max-width: 92vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">동일 조건 SKU가 이미 있습니다</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <div class="text-body2 q-mb-sm">
            입력한 브랜드, 카테고리, HS CODE, 규격 조건과 같은 SKU가 이미 등록되어 있습니다.
          </div>
        <div class="text-caption text-grey-7 q-mb-md">
            동일 조건의 기준 상품이 이미 있으므로 신규 등록 요청이 아니라 연결 요청으로 제출됩니다.
          </div>
          <CandidateList
            :candidates="submitCandidates"
            @select="onCandidateSelect"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="취소" v-close-popup />
          <q-btn
            color="positive"
            :label="duplicateActionLabel"
            :loading="submitting"
            @click="confirmDuplicateSubmit"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <PageGuideDialog
      v-model="guideDialog"
      title="SKU 요청 생성 사용 가이드"
      description="새로운 내 SKU를 등록 또는 연결 요청하는 방법입니다."
    >
      <PartnerFulfillmentGuide guide-key="requestNew" />
    </PageGuideDialog>
  </q-layout>
</template>
